import { createSelector } from '@ngrx/store';

import { zonesFeature } from 'src/app/core/zones/zones.reducer';
import { unifyNumberFormat } from 'src/app/job/job-helpers';
import { MergeConflictFieldOptions } from 'src/app/users/user-page/user-page-merge-conflict-dialog/user-page-merge-conflict-dialog.state';
import { FullUserWithZoneParentFragment } from 'src/generated/graphql.generated';

import { userPageSimilarCustomersFeature } from './user-page-similar-customers.feature';
import { UserPageSimilarCustomersSourceUser } from './user-page-similar-customers.state';

// Mirrors the role/zone checks in backend/src/services/user-manager/users.merge.ts (validateRolesBeforeUserMerge).
const EMPLOYEE_ROLE_ATTRIBUTE = 'employee';

type MergeCandidateRoles = FullUserWithZoneParentFragment['roles'];

const roleBelongsToZone = (role: NonNullable<MergeCandidateRoles>[number], zoneId: string): boolean =>
  !!role.zones?.nodes.some((zone) => zone?.id === zoneId);

const hasEmployeeRole = (roles: MergeCandidateRoles): boolean =>
  !!roles?.some((role) => role.attributes?.includes(EMPLOYEE_ROLE_ATTRIBUTE));

//check in backend retrieves all roles including roles from siblings franchise
//in fe roles from sibling franchises are not retrieved, but zones are
const hasRoleOutsideZone = (roles: MergeCandidateRoles, zoneId: string): boolean =>
  !!roles?.some((role) => !roleBelongsToZone(role, zoneId));

type MergeCandidateZones = FullUserWithZoneParentFragment['zones'];

// A user can be directly in the current franchise zone, and/or in that franchise's area zone
// (a child zone). Neither counts as "outside" the current zone — only a zone belonging to a
// different franchise does.
const hasZoneOutsideCurrentZone = (zones: MergeCandidateZones, zoneId: string): boolean =>
  !!zones?.nodes.some((zone) => zone?.id !== zoneId && zone?.parent?.id !== zoneId);

const normalizeText = (value?: string | null) => value?.trim().toLowerCase() ?? '';

const trimmedOrEmpty = (value?: string | null) => value?.trim() ?? '';

const formatBillingAddress = (address?: { addressLineOne?: string | null; addressLineTwo?: string | null } | null) =>
  [ address?.addressLineOne, address?.addressLineTwo ]
    .map(trimmedOrEmpty)
    .filter(Boolean)
    .join(', ');

const detectFieldConflict = (
  primaryValue: string,
  duplicateValue: string,
  primaryId?: string,
  duplicateId?: string,
): MergeConflictFieldOptions | undefined => {
    if (!primaryValue || !duplicateValue || primaryValue.toLowerCase() === duplicateValue.toLowerCase()) {
        return undefined;
    }
    return { primaryValue, duplicateValue, primaryId, duplicateId };
};

export const detectMergeConflicts = (
  sourceUser: UserPageSimilarCustomersSourceUser,
  duplicate: FullUserWithZoneParentFragment,
) => ({
    company: detectFieldConflict(trimmedOrEmpty(sourceUser.company), trimmedOrEmpty(duplicate.company)),
    billingAddress: detectFieldConflict(
      formatBillingAddress(sourceUser.billingAddress),
      formatBillingAddress(duplicate.billingAddress),
      sourceUser.billingAddress?.id,
      duplicate.billingAddress?.id,
    ),
});

const isExactMatch = (
  candidate: FullUserWithZoneParentFragment,
  sourceUser: UserPageSimilarCustomersSourceUser,
): boolean => {
    const sameName = normalizeText(candidate.givenName) === normalizeText(sourceUser.givenName)
      && normalizeText(candidate.familyName) === normalizeText(sourceUser.familyName);

    const sameEmail = !!candidate.email && normalizeText(candidate.email) === normalizeText(sourceUser.email);
    const samePhone = !!candidate.phone && unifyNumberFormat(candidate.phone) === unifyNumberFormat(sourceUser.phone || '');

    return sameName && sameEmail && samePhone;
};

const getMergeDisabledTooltip = (
  candidate: FullUserWithZoneParentFragment,
  sourceUser: UserPageSimilarCustomersSourceUser,
  currentZoneId?: string,
): string | null => {
    const sourceNiceJobsId = sourceUser.metadata?.niceJobsId;
    const candidateNiceJobsId = candidate.metadata?.niceJobsId;

    if (sourceNiceJobsId && candidateNiceJobsId && sourceNiceJobsId !== candidateNiceJobsId) {
        return 'Customers are connected to different NiceJobs profiles and can\'t be merged.';
    }

    const roles = [ ...(sourceUser.roles ?? []), ...(candidate.roles ?? []) ];

    if (hasEmployeeRole(roles)) {
        return 'Customers with an employee role can\'t be merged.';
    }

    if (currentZoneId && hasRoleOutsideZone(roles, currentZoneId)) {
        return 'Customer has roles in multiple zones and can\'t be merged.';
    }

    if (
      currentZoneId
      && (hasZoneOutsideCurrentZone(sourceUser.zones, currentZoneId) || hasZoneOutsideCurrentZone(candidate.zones, currentZoneId))
    ) {
        return 'Customer belongs to multiple zones and can\'t be merged.';
    }

    return null;
};

export const selectSimilarCustomersWithMatch = createSelector(
  userPageSimilarCustomersFeature.selectCustomers,
  userPageSimilarCustomersFeature.selectSourceUser,
  zonesFeature.selectCurrentAppZone,
  (customers, sourceUser, currentAppZone) => customers
    .filter((candidate) => candidate.id !== sourceUser?.id)
    .map((candidate) => {
        const mergeDisabledTooltip = sourceUser ? getMergeDisabledTooltip(candidate, sourceUser, currentAppZone?.id) : null;

        return {
            ...candidate,
            isExactMatch: sourceUser ? isExactMatch(candidate, sourceUser) : false,
            mergeButtonDisabled: Boolean(mergeDisabledTooltip),
            mergeButtonDisabledTooltip: mergeDisabledTooltip,
        };
    }),
);
